import { Mail, ShieldCheck, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ncoder/ui';
import type {
  OrgAdminPeopleSection,
  OrgAdminSelfContext,
} from '@/components/molecules/org-admin/types';
import { PEOPLE_SECTIONS } from '@/components/molecules/org-admin/adminTabs';
import { MembersPanel } from './MembersPanel';
import { RolesPanel } from './RolesPanel';
import { InvitationsPanel } from './InvitationsPanel';

/**
 * People — the roster, the roles that group it, and the invitations that will join it.
 *
 * WHY THESE THREE ARE ONE DESTINATION. They are three views of one object graph: a member holds a
 * role, an invitation carries the role a future member will hold. As peer tabs they took three of
 * seven slots for one job, and two of them are setup-time screens — the panel's own tab-ordering
 * note observes that an admin who opens this page ten times a month configures roles once. Every
 * comparable product (GitHub, Linear, Notion, Stripe, Supabase) nests them for the same reason.
 *
 * THE COST, AND THE TWO MITIGATIONS. Nesting genuinely hides things, and pretending otherwise would
 * be the wrong trade to make silently:
 *
 *   - Someone hunting permissions may scan for "Access" or "Roles" and not read "People" as covering
 *     it. So the sub-tab is labelled "Roles & access" — the word is visible one level down — and
 *     Overview keeps its Roles preview card with a "Manage" link as a second entry point.
 *   - A pending invitation expires, so burying it is a real regression. Its count badge is therefore
 *     promoted to the People tab in `OrgAdminLayout`, which means an outstanding invite is visible
 *     without entering this section at all.
 *
 * **The three panels below are mounted unchanged.** This component moves a container, not markup.
 */

export interface PeoplePanelProps {
  self: OrgAdminSelfContext;
  section: OrgAdminPeopleSection;
  onSelectSection: (section: OrgAdminPeopleSection) => void;
  /** Pending invitations, shown on the Invitations sub-tab the same way the old tab bar showed it. */
  pendingInvitations: number;
  memberCount: number | null;
  roleCount: number | null;
}

const SECTION_META: Record<
  OrgAdminPeopleSection,
  { label: string; icon: typeof Users }
> = {
  members: { label: 'Members', icon: Users },
  // "Roles & access", not "Roles": this is the discoverability mitigation described above — an admin
  // looking for permissions needs to see a word they recognise once they are inside People.
  roles: { label: 'Roles & access', icon: ShieldCheck },
  invitations: { label: 'Invitations', icon: Mail },
};

export function PeoplePanel({
  self,
  section,
  onSelectSection,
  pendingInvitations,
  memberCount,
  roleCount,
}: PeoplePanelProps) {
  const counts: Record<OrgAdminPeopleSection, { value: number | null; tone?: 'warn' }> = {
    members: { value: memberCount },
    roles: { value: roleCount },
    // The one count that means "something is outstanding here", so the one that gets a colour.
    invitations: {
      value: pendingInvitations || null,
      tone: pendingInvitations ? 'warn' : undefined,
    },
  };

  return (
    <Tabs
      value={section}
      onValueChange={(value) => onSelectSection(value as OrgAdminPeopleSection)}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/*
        `overflow-x-auto` with `flex-nowrap`, not a wrapping row.

        This is a second navigation row under the main tab bar, and at tablet and phone widths three
        sub-tabs plus four top-level tabs do not both fit. A wrapping strip pushes the panel down by a
        whole row and reads as a layout accident; a scrolling strip keeps one row and stays reachable,
        which is the trade the responsive rules name for exactly this case. `shrink-0` on the list
        keeps it out of the panel's flex height calculation.
      */}
      <TabsList
        aria-label="People sections"
        className="oa-subtablist h-auto shrink-0 flex-nowrap justify-start overflow-x-auto rounded-none bg-transparent p-0"
      >
        {PEOPLE_SECTIONS.map((value) => {
          const { label, icon: Icon } = SECTION_META[value];
          const count = counts[value];
          return (
            <TabsTrigger
              key={value}
              value={value}
              className="oa-subtab shrink-0 rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {label}
              {count.value !== null && (
                <span className="oa-tab-count" data-tone={count.tone}>
                  {count.value}
                </span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {/*
        Same reasoning as the outer bar: no `forceMount`, because each panel owns its own search and
        sort state and an admin expects a search box to reset when they leave the list it filtered.
      */}
      {PEOPLE_SECTIONS.map((value) => (
        <TabsContent
          key={value}
          value={value}
          className="mt-0 flex min-h-0 flex-1 flex-col focus-visible:outline-none data-[state=inactive]:hidden"
        >
          {value === 'members' && <MembersPanel self={self} />}
          {value === 'roles' && <RolesPanel self={self} />}
          {value === 'invitations' && <InvitationsPanel self={self} />}
        </TabsContent>
      ))}
    </Tabs>
  );
}
